import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button, Textarea, useToast } from '../../components/common';
import CruxFeatureTable from './CruxFeatureTable';
import { CRUX_SECTIONS, SECTION_GROUPS, groupOf, cruxProgress } from '../../constants/cruxSections';
import { CRUX_GUIDANCE } from '../../constants/cruxGuidance';
import api from '../../services/api';
import './CruxTab.css';

// Taken from the registry rather than written out again: the pattern is the
// storage key, and a copy that drifts silently edits a different section.
const FEATURES_PATTERN = (CRUX_SECTIONS.find(s => s.label === 'Platform features') || {}).pattern;

/**
 * The CRUX — the design document an app's permissions are derived from.
 *
 * Reads and writes the same `crux` object `paradigm crux push` stores, so a
 * document written in the repo and one edited here are the same document. Editing
 * is section at a time: a nine-chapter design doc presented as one textarea is how
 * it stays unfinished.
 *
 * Sections the registry does not recognise are carried through untouched — the CLI
 * may know about a section this build does not.
 */
function CruxTab({ appId, appName, specVersion, onStaged }) {
  const toast = useToast();
  const [crux, setCrux] = useState(null);
  const [saved, setSaved] = useState(null);
  const [activePattern, setActivePattern] = useState(CRUX_SECTIONS[0].pattern);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [viewingStaged, setViewingStaged] = useState(false);
  // READ is the default: the design doc as a document, each section rendered
  // from the markdown it is stored as. EDIT is the per-section editor.
  const [mode, setMode] = useState('read');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // The staged document wins over the live one. Loading the live CRUX
        // while a staged version existed showed the old text, so staged edits
        // looked discarded — and editing again staged live-plus-the-new-edit,
        // silently dropping everything staged before it.
        const [res, draft] = await Promise.all([
          api.request(`/third-party/${appId}/crux`),
          api.request(`/third-party/${appId}/draft`).catch(() => null),
        ]);
        if (cancelled) return;
        const stagedCrux = (draft?.changes || []).find(c => c.field === 'crux')?.to;
        const doc = stagedCrux || res.crux || null;
        setViewingStaged(Boolean(stagedCrux));
        setCrux(doc);
        setSaved(JSON.stringify(doc?.sections || {}));
      } catch (err) {
        if (!cancelled && err.status !== 404) {
          toast.error(err.message || 'Could not load the CRUX.');
        }
        if (!cancelled) { setCrux(null); setSaved('{}'); }
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [appId, toast]);

  const sections = crux?.sections || {};
  const progress = useMemo(() => cruxProgress(sections), [sections]);
  const dirty = JSON.stringify(sections) !== saved;

  const setSection = useCallback((pattern, text) => {
    setCrux(prev => ({
      format: prev?.format || 'md',
      ...prev,
      sections: { ...(prev?.sections || {}), [pattern]: text },
    }));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Empty sections are dropped, matching what the CLI stores — an unwritten
      // section is absent rather than present and blank.
      const trimmed = Object.fromEntries(
        Object.entries(crux?.sections || {}).filter(([, v]) => String(v || '').trim())
      );
      // Derived, like the access request's. A box asking you to describe a
      // change the document already records goes unfilled, and history fills
      // with a default.
      const was = JSON.parse(saved || '{}');
      const label = p => (CRUX_SECTIONS.find(x => x.pattern === p) || {}).label || 'a section';
      const wrote = Object.keys(trimmed).filter(k => !String(was[k] || '').trim()).map(label);
      const edited = Object.keys(trimmed).filter(k => String(was[k] || '').trim() && was[k] !== trimmed[k]).map(label);
      const cleared = Object.keys(was).filter(k => String(was[k] || '').trim() && !trimmed[k]).map(label);
      const parts = [];
      if (wrote.length) parts.push(`wrote ${wrote.slice(0, 3).join(', ')}${wrote.length > 3 ? ` and ${wrote.length - 3} more` : ''}`);
      if (edited.length) parts.push(`revised ${edited.slice(0, 3).join(', ')}${edited.length > 3 ? ` and ${edited.length - 3} more` : ''}`);
      if (cleared.length) parts.push(`cleared ${cleared.slice(0, 2).join(', ')}`);
      // Staged with everything else. The review still runs on commit, because
      // that is when the document actually changes.
      await api.request(`/third-party/${appId}/draft`, {
        method: 'PATCH',
        body: JSON.stringify({ crux: { format: 'md', ...crux, sections: trimmed } }),
      });
      setSaved(JSON.stringify(trimmed));
      setCrux(c => ({ ...c, sections: trimmed }));
      toast.success('CRUX staged.');
      setViewingStaged(true);
      // Tell the app page, or the staged-changes bar will not know it moved.
      onStaged?.();
    } catch (err) {
      toast.error(err.message || 'Could not save.');
    }
    setSaving(false);
  };

  if (loading) return <div className="crux__loading">Loading…</div>;

  const active = CRUX_SECTIONS.find(s => s.pattern === activePattern) || CRUX_SECTIONS[0];
  const hasAny = Object.values(sections).some(v => String(v || '').trim());

  const chapters = SECTION_GROUPS.map((g, i) => {
    const rows = CRUX_SECTIONS.filter(x => x.score === g.score);
    const req = rows.filter(x => x.required);
    const done = req.filter(x => String(sections[x.pattern] || '').trim());
    return { n: i, title: g.title, of: g.of, rows, required: req.length, done: done.length };
  }).filter(c => c.rows.length);

  const activeGroup = groupOf(activePattern);

  // Open one section in the editor (from a section's Edit, "Write it", the
  // next-unwritten link, or the rail while editing).
  const editSection = (pattern) => { setActivePattern(pattern); setMode('edit'); };
  // The rail: in read mode it moves the document to the section; editing, it
  // opens the section.
  const pickSection = (pattern) => {
    if (mode === 'edit') { setActivePattern(pattern); return; }
    const i = CRUX_SECTIONS.findIndex(x => x.pattern === pattern);
    document.getElementById(`crux-sec-${i}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="crux">
      <header className="crux__head">
        <div className="crux__progress">
          <div className="crux__progress-line">
            <strong>{progress.requiredDone}</strong> of {progress.requiredTotal} required sections written
          </div>
          <div className="crux__bar" role="progressbar" aria-valuenow={progress.percent} aria-valuemin={0} aria-valuemax={100}>
            <span style={{ width: `${progress.percent}%` }} />
          </div>
        </div>
        <div className="crux__head-actions">
          {progress.nextUnfinished && (
            <button className="crux__link" onClick={() => editSection(progress.nextUnfinished.pattern)}>
              Next unwritten: {progress.nextUnfinished.label}
            </button>
          )}
          {dirty && (
            <Button variant="secondary" onClick={() => setCrux(c => ({ ...c, sections: JSON.parse(saved) }))}>
              Reset
            </Button>
          )}
          {mode === 'read'
            ? <Button variant="secondary" onClick={() => setMode('edit')}>Edit markdown</Button>
            : <Button variant="secondary" onClick={() => setMode('read')}>Done</Button>}
          {(mode === 'edit' || dirty) && (
            <Button onClick={save} disabled={!dirty || saving}>
              {saving ? 'Staging…' : dirty ? 'Stage Changes' : 'Staged'}
            </Button>
          )}
        </div>
      </header>

      {viewingStaged && (
        <p className="crux__staged-note">
          You are editing the staged CRUX, not the one the app is serving. It goes live when you
          commit.
        </p>
      )}

      {!hasAny && (
        <p className="crux__intro">
          Nothing written yet for {appName}. A CRUX says what this app is for, where it sits in the
          identity pipeline, which schemas it reuses and which workflows it completes — and section 9
          is what the consent screen is derived from. Scaffold one with <code>paradigm crux init</code>,
          or start writing here.
        </p>
      )}

      <div className="crux__body">
        {/* One chapter open at a time. All twenty-six sections listed at once ran
            off the bottom of the page and gave no sense of how much of the
            document each part accounted for. */}
        <nav className="crux__rail" aria-label="CRUX sections">
          {chapters.map(c => {
            // Every group stays expanded. Sixteen rows fit, and collapsing them
            // hid the shape of the document behind the one part you happened to
            // be in — you could not see that the claim was empty while writing
            // the schemas. The group of the active section is only highlighted.
            const here = c.title === activeGroup.title;
            return (
              <div key={c.n} className={`crux__chapter is-open ${here ? 'is-here' : ''}`}>
                <button
                  className="crux__chapter-head"
                  onClick={() => pickSection(c.rows[0].pattern)}
                >
                  <span className="crux__chapter-title">{c.title}</span>
                  {c.required > 0 && (
                    <span className={`crux__chapter-count ${c.done === c.required ? 'is-done' : ''}`}>
                      {c.done}/{c.required}
                    </span>
                  )}
                </button>
                {c.rows.map(x => {
                  const written = String(sections[x.pattern] || '').trim().length > 0;
                  return (
                    <button
                      key={x.pattern}
                      className={`crux__row ${mode === 'edit' && x.pattern === activePattern ? 'is-active' : ''}`}
                      onClick={() => pickSection(x.pattern)}
                    >
                      <span className="crux__row-title">{x.label}</span>
                      {/* A mark, not a word on every row: filled is written, hollow
                          is required and missing, nothing is optional. The word
                          stays available on hover and to a screen reader. */}
                      {(written || x.required) && (
                        <span
                          className={`crux__row-mark ${written ? 'is-done' : 'is-todo'}`}
                          title={written ? 'Written' : 'Required, not written'}
                          role="img"
                          aria-label={written ? 'Written' : 'Required, not written'}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {mode === 'read' ? (
          <article className="crux__doc" aria-label="Design doc">
            {CRUX_SECTIONS.map((x, i) => {
              const text = String(sections[x.pattern] || '').trim();
              return (
                <section key={x.pattern} id={`crux-sec-${i}`} className="crux__docsec">
                  <h3 className="crux__dochead">
                    <span className="crux__docnum">§{i + 1}</span>
                    {x.label}
                    {!x.required && <span className="crux__docopt">optional</span>}
                    <button type="button" className="crux__docedit" onClick={() => editSection(x.pattern)}>Edit</button>
                  </h3>
                  {text ? (
                    <div className="crux__md">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
                    </div>
                  ) : x.required ? (
                    <div className="crux__todo">
                      <b>Not written yet.</b>
                      {CRUX_GUIDANCE[x.pattern] && <span> {CRUX_GUIDANCE[x.pattern]}</span>}
                      <button type="button" className="crux__docedit is-shown" onClick={() => editSection(x.pattern)}>Write it</button>
                    </div>
                  ) : (
                    <p className="crux__docempty">Not written. Optional.</p>
                  )}
                </section>
              );
            })}
          </article>
        ) : (
        <section className="crux__pane">
          <div className="crux__pane-head">
            <div>
              <h3>{active.label}</h3>
              <p className="crux__where">
                {activeGroup.title}
                {activeGroup.of && <> · evidence for <b>{activeGroup.of}</b></>}
              </p>
            </div>
            <span className={`crux__tag ${String(sections[active.pattern] || '').trim()
              ? 'is-done' : active.required ? 'is-todo' : 'is-optional'}`}>
              {String(sections[active.pattern] || '').trim()
                ? 'Written' : active.required ? 'Required' : 'Optional'}
            </span>
          </div>

          {CRUX_GUIDANCE[active.pattern] && (
            <p className="crux__guide">{CRUX_GUIDANCE[active.pattern]}</p>
          )}

          {/* Sections whose answers are a table get a form. Everything else is
              plain text — markdown is what gets stored, and a preview of your
              own prose is not worth the pane it sits in. */}
          {active.pattern === FEATURES_PATTERN ? (
            <CruxFeatureTable
              value={sections[active.pattern] || ''}
              onChange={v => setSection(active.pattern, v)}
            />
          ) : (
            <Textarea
              className="crux__editor"
              value={sections[active.pattern] || ''}
              onChange={e => setSection(active.pattern, e.target.value)}
              rows={20}
              placeholder="Plain text or markdown. Written here or in the repo — the same document either way."
            />
          )}
        </section>
        )}
      </div>
    </div>
  );
}

export default CruxTab;
